import React, { useMemo, useState } from 'react';
import {
  CatalogueView,
  dispatch,
  ErrorVariants,
  getCatalogConfig,
  PricingStrategy,
  ProductVariant,
  showToast,
  useAppSelector,
} from '@zonofi/zono-money-store';
import './customer-product-card.scss';
import {
  PriceInfoTooltip,
  ProductImage,
  SpinnerLoader,
} from '../../components';
import { ProductSegments } from '../product-segments/product-segments';
import { CategoryTags } from '../category-tags';
import { getFormattedAmount } from '@zonofi/common/functions';
import { TouchableOpacity } from '../../common';
import { QuantityEditorCard } from '../../order/quantity-editor/quantity-editor';
import { mapProductVariantToOrderProduct } from '../../helper';
import { ProductFacets } from '../product-facets/product-facets';
import { OrderProductActionType } from '../../constants';
import { ReactComponent as DeleteIcon } from '@zonofi/svg-icons/delete.svg';
import { getProductVariantStock } from '../../helper/parse-inventory-stock';

interface CustomerProductCardProps {
  data: ProductVariant;
  onAddProduct?: (item: ProductVariant) => void;
  isAdding?: boolean;
  isRemoving?: boolean;
  isInCart?: boolean;
  handleUpdate: (
    updatedLine: ProductVariant,
    type?: OrderProductActionType
  ) => void;
  openUomModal?: () => void;
  /** From Catalogue module config; when omitted, selector value is used. */
  showOutOfStockTag?: boolean;
}

export const CustomerProductCard: React.FC<CustomerProductCardProps> = ({
  data,
  onAddProduct,
  isAdding,
  isRemoving,
  isInCart,
  handleUpdate,
  openUomModal,
  showOutOfStockTag,
}) => {
  const catalogueConfig = useAppSelector(getCatalogConfig);
  const effectiveShowOutOfStockTag =
    showOutOfStockTag ?? catalogueConfig.showOutOfStockTag;
  const showImage = useMemo(
    () => catalogueConfig.catalogueView === CatalogueView.ListWithImagesView,
    [catalogueConfig.catalogueView]
  );
  const productMOQ = useMemo(() => data?.minOrderQty || 1, [data]);
  const orderProduct = useMemo(
    () => mapProductVariantToOrderProduct(data),
    [data]
  );

  const [editState, setEditState] = useState({ price: false, quantity: false });
  const [inputState, setInputState] = useState({ price: '', quantity: 0 });

  const isOutOfStock = useMemo(() => {
    if (!effectiveShowOutOfStockTag) return false;
    const stock = getProductVariantStock(data);
    return typeof stock === 'number' && stock < 1;
  }, [effectiveShowOutOfStockTag, data]);

  const onMinusPresss = () => {
    if (Number(data?.quantity) - productMOQ >= productMOQ) {
      handleUpdate(
        {
          ...data,
          newQty: Number(data?.quantity) - productMOQ,
        },
        OrderProductActionType.EDIT
      );
    }
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
  };

  const onPlusPress = () => {
    handleUpdate(
      {
        ...data,
        newQty: Number(data?.quantity) + productMOQ,
      },
      OrderProductActionType.EDIT
    );
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
  };

  const onInputQuantityUpdate = () => {
    if (inputState.quantity && inputState.quantity !== data?.quantity) {
      if (inputState.quantity.toString().length > 3) {
        dispatch(
          showToast({
            message: 'Product quantity should be less than 1000',
            type: ErrorVariants.DANGER,
            canDisplay: true,
            responseStatus: 'Error',
          })
        );
      } else {
        handleUpdate(
          {
            ...data,
            newQty: inputState.quantity,
          },
          OrderProductActionType.EDIT
        );
      }
    }
  };

  const onAddPress = () => {
    if (isOutOfStock) return;
    onAddProduct?.(data);
  };

  return (
    <div className="customer-product-card">
      <div className="customer-product-card-data">
        {showImage && (
          <ProductImage productImage={data?.assets?.[0]?.redirectUrl} />
        )}

        <div>
          <div className="customer-product-card-name font-semibold">
            {data?.name}
          </div>
          {(data?.facets?.length > 0 ||
            (data?.segments ?? [])?.length > 0 ||
            data?.categories?.length > 0) && (
            <div className="customer-product-meta-row">
              {data?.facets?.length > 0 && (
                <ProductFacets facets={data?.facets} isProductPricing={true} />
              )}
              {isOutOfStock && (
                <div className="customer-product-out-of-stock-wrap">
                  <span className="customer-product-out-of-stock-tag font-semibold">
                    OUT OF STOCK
                  </span>
                </div>
              )}
              {data?.facets?.length > 0 &&
                (data?.segments ?? [])?.length > 0 && (
                  <div className="customer-product-meta-divider" />
                )}
              {(data?.segments ?? [])?.length > 0 && (
                <ProductSegments segments={data.segments ?? []} />
              )}
              {(data?.segments ?? [])?.length > 0 &&
                data?.categories?.length > 0 && (
                  <div className="customer-product-meta-divider" />
                )}
              {data?.categories?.length > 0 && (
                <CategoryTags categories={data.categories} />
              )}
            </div>
          )}

          <div className="customer-product-price-view">
            <span className="customer-product-card-price font-semibold">
              {`Price: `}
              <span className="customer-product-card-price-value">
                {getFormattedAmount(data?.basePrice)}
              </span>
            </span>

            {data?.pricingStrategy !== PricingStrategy.STANDARD && (
              <PriceInfoTooltip
                title={'Computed Price'}
                subText={
                  <>quantity × price = {getFormattedAmount(data?.price)}</>
                }
                heading={`(${getFormattedAmount(data?.price)})`}
              />
            )}
          </div>
        </div>
      </div>

      <div className="customer-product-card-actions">
        {isInCart ? (
          <div className="customer-product-card-edit-card">
            <QuantityEditorCard
              quantity={
                editState.quantity
                  ? inputState.quantity
                  : Number(data?.quantity)
              }
              price={
                (editState.quantity
                  ? inputState.quantity
                  : Number(data?.quantity)) * Number(data?.price)
              }
              onQuantityChange={(val) => {
                setEditState((prev) => ({ ...prev, quantity: true }));
                setInputState((prev) => ({ ...prev, quantity: val }));
              }}
              onInputBlur={() => {
                onInputQuantityUpdate();
                setEditState((prev) => ({ ...prev, quantity: false }));
              }}
              disableStepper={isOutOfStock}
              onMinusPress={onMinusPresss}
              onPlusPress={onPlusPress}
              disable={
                (editState.quantity
                  ? inputState.quantity
                  : Number(data?.quantity)) <= productMOQ
              }
              openUomModal={openUomModal}
              product={orderProduct}
            />
            {isRemoving ? (
              <SpinnerLoader
                color={'#DD360C'}
                size={18}
                className="remove-spinner-loader"
              />
            ) : (
              <div
                onClick={() =>
                  handleUpdate(data, OrderProductActionType.REMOVE)
                }
              >
                <DeleteIcon color="#DD360C" />
              </div>
            )}
          </div>
        ) : (
          <TouchableOpacity
            className={`customer-product-card-add-button ${
              isAdding ? 'loading' : 'normal'
            } ${isOutOfStock ? 'disabled' : ''}`}
            onPress={onAddPress}
          >
            {isAdding ? (
              <SpinnerLoader color={'#000'} size={18} />
            ) : (
              <div className="customer-product-card-add-button-text font-medium">
                Add
              </div>
            )}
          </TouchableOpacity>
        )}
      </div>
    </div>
  );
};
